/**
 * Org-level AI goal-generation rules (goal-customization-ai, PROC-115;
 * per-template instructions: PROA-2618 stage 1).
 *
 * The general blob and the AI switches are ORG-WIDE. Per-field
 * instructions are layered: the ORG-DEFAULT map (edited by the "Shared
 * across templates" section via `shared_fields` / `org_field_instructions`)
 * is the default every template inherits; each (template, field) row can
 * override it with its own text, addressed by `template_id` in the nested
 * PUT shape. Rows report provenance via `instructions_source`.
 *
 * Hand-written client for `/basecamp/organizations/:orgId/goal-generation-rules`
 * — the endpoint is newer than the generated `types-and-hooks.ts` snapshot
 * (same pattern as `prompt-test-bed.ts`). Fold into the generated client on
 * the next Orval regen.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { customFetch } from "@api/rest/custom-fetcher";

export interface GoalGenerationRulesGoalField {
  /** Stable row key: field_identifier, falling back to title for custom
   *  fields created without an identifier. Use for React keys + the AI toggle. */
  key: string;
  field_identifier: string;
  title: string | null;
  /** True when the AI fills this field (is_ai_generated on the template field). */
  ai: boolean;
  /** True when the field is a template custom field rather than a native ReportGoal column. */
  custom: boolean;
  /** False = switch locked; see lock_reason for why. */
  editable: boolean;
  /** Why the switch is locked, or null when editable. */
  lock_reason: "core" | "platform" | "not_text" | null;
  /** not_text locks still accept turning the switch OFF (drift cleanup). */
  can_turn_off: boolean;
  /** This row's RESOLVED per-template instructions — display as received.
   *  The backend does the layering: an org-linked template's own row text
   *  wins, empty falls back to the org-level default map (one empty state,
   *  no clear sentinel); on shared/base templates the org-level map
   *  overrides the platform-authored text. Saved texts are concatenated
   *  onto the general blob as "Title: text" where the LLM consumes the
   *  rules. */
  instructions?: string | null;
  /** Where the displayed `instructions` came from: "template" = this row's
   *  own text resolved; "org" = inherited from the org-level default map
   *  (badge it — typing creates a template override); null = nothing
   *  resolved. */
  instructions_source: "template" | "org" | null;
  /** This field's enablement ON THIS TEMPLATE — false hides the row for
   *  that template (re-enable it on the Programming tab for it to generate).
   *  Per-template, unlike the org-wide AI switch. */
  is_enabled: boolean;
  /** The template this row belongs to — the page groups fields under it. */
  template_name: string | null;
  /** This template's report type — IA and reassessment templates both run
   *  goal generation and appear here. Part of the bucket identity: a
   *  same-named IA + reassessment pair renders as two (badged) buckets. */
  report_type: "INITIAL_ASSESSMENT" | "REASSESSMENT";
  /** The TREATMENT PLAN TEMPLATE pk (as string) this row hangs off — the
   *  write address for per-template instructions in the nested PUT shape.
   *  Always present. Several same-named report templates can share one
   *  template_id (publishing duplicates report templates, not TPT rows),
   *  so rows in different display buckets may carry the same address —
   *  their instructions state is genuinely shared. */
  template_id: string;
}

/** One entry per field carried (enabled) by EVERY template shown in
 *  `fields` — the "Shared across templates" section. It edits the
 *  ORG-DEFAULT layer: the text template rows fall back to when they carry
 *  none of their own (and the org's override on shared/base templates).
 *  Same org-wide per-key meta as the rows; no per-template attributes and
 *  no AI switch of its own (switches live on template rows). */
export interface GoalGenerationRulesSharedGoalField {
  key: string;
  field_identifier: string;
  title: string | null;
  ai: boolean;
  custom: boolean;
  editable: boolean;
  lock_reason: "core" | "platform" | "not_text" | null;
  can_turn_off: boolean;
  /** The RAW org-level default text for this key — what PUT
   *  `org_field_instructions` edits. NOT resolved against any template row;
   *  null when the org map has no entry. */
  org_instructions: string | null;
}

export interface GoalGenerationRules {
  general: string | null;
  /** One row per (template, field): every template's configured GOAL fields,
   *  grouped per template on the page. Core + disabled fields included; the
   *  AI switch is org-wide (keyed by field key), enablement and instructions
   *  are per-template (addressed by template_id). */
  fields: GoalGenerationRulesGoalField[];
  /** The org-default layer's edit surface: fields enabled on every template
   *  bucket in `fields` (intersection). Always present ([] when there are
   *  no templates), key-alphabetical. */
  shared_fields: GoalGenerationRulesSharedGoalField[];
}

export interface PutGoalGenerationRulesInput {
  general?: string | null;
  /** Desired is_ai_generated per union-row key — only changed keys needed.
   *  ORG-WIDE (a field's switch is shared across every template carrying
   *  it). Validated with `general` and applied atomically. */
  ai_fields?: Record<string, boolean>;
  /** Per-field TEMPLATE-LEVEL instructions, NESTED per template:
   *  `{ template_id: { key: text } }`. Whole-map semantics PER INCLUDED
   *  template — send every key the template carries; blank means "no
   *  template-level text" (the row falls back to the org-default map), so
   *  untouched inherited rows send "" and org text is never materialized
   *  onto template rows. The backend routes writes by template kind
   *  (org-linked templates write their own rows; base/shared templates
   *  rewrite the org-level map entries) — the SPA doesn't need to know
   *  which is which. (The legacy flat `{key: text}` shape is still
   *  accepted server-side for deploy skew, but this client only sends
   *  nested.) */
  field_instructions?: Record<string, Record<string, string>>;
  /** ORG-DEFAULT layer writes (`{key: text}`) — the "Shared across
   *  templates" section. MERGE semantics: submitted keys replace their
   *  org-map entry, a blank string DELETES the default, unsubmitted keys
   *  are preserved. Applied AFTER the nested field_instructions in the
   *  same PUT (an explicit org edit wins a collision). NOT combinable with
   *  the legacy flat field_instructions shape (400) — this client sends
   *  nested + org_field_instructions only. */
  org_field_instructions?: Record<string, string>;
}

type Envelope<T> = { data: T | { detail?: string }; status: number };

async function unwrap<T>(resp: Envelope<T>): Promise<T> {
  if (resp.status !== 200) {
    const body = resp.data as { detail?: string } | Record<string, string[]> | undefined;
    // DRF returns {detail} for view-level errors and {field: [msgs]} for
    // serializer validation — surface whichever exists.
    const firstFieldError =
      body && !("detail" in body)
        ? Object.values(body)
            .flat()
            .find((v) => typeof v === "string")
        : undefined;
    throw new Error((body as { detail?: string })?.detail ?? firstFieldError ?? `HTTP ${resp.status}`);
  }
  return resp.data as T;
}

const url = (orgId: string) => `/api/basecamp/organizations/${orgId}/goal-generation-rules`;

export const goalGenerationRulesQueryKey = (orgId: string) => [url(orgId)] as const;

export function useGoalGenerationRules(orgId: string, enabled = true) {
  return useQuery({
    queryKey: goalGenerationRulesQueryKey(orgId),
    queryFn: async () => unwrap(await customFetch<Envelope<GoalGenerationRules>>(url(orgId))),
    enabled: !!orgId && enabled,
  });
}

export function useUpdateGoalGenerationRules(orgId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: PutGoalGenerationRulesInput) =>
      unwrap(
        await customFetch<Envelope<GoalGenerationRules>>(url(orgId), {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(input),
        }),
      ),
    onSuccess: (data) => {
      queryClient.setQueryData(goalGenerationRulesQueryKey(orgId), data);
    },
  });
}
